import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { apiFetch } from '../api/client.js';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { LANGUAGE_NAMES } from '../i18n/languages.js';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';
import PageMeta from '../components/PageMeta.jsx';

function StatusBadge({ status, t }) {
  const map = {
    subscriber: { cls: 'badge-success', label: t('dashboard.subscription.statusSubscriber') },
    free:       { cls: 'badge-info',    label: t('dashboard.subscription.statusFree') },
    expired:    { cls: 'badge-danger',  label: t('dashboard.subscription.statusExpired') },
    inactive:   { cls: 'badge-warning', label: t('dashboard.subscription.statusInactive') },
  };
  const { cls, label } = map[status] ?? {
    cls: 'badge-warning',
    label: t('dashboard.subscription.statusUnknown'),
  };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function Row({ label, children }) {
  return (
    <tr>
      <th
        scope="row"
        style={{ color: 'var(--color-muted)', paddingRight: '1.5rem', paddingBottom: '0.7rem', whiteSpace: 'nowrap', fontWeight: 400, textAlign: 'left' }}
      >
        {label}
      </th>
      <td style={{ paddingBottom: '0.7rem' }}>{children}</td>
    </tr>
  );
}

function ToolScopeDisplay({ scope, t }) {
  if (!scope) return <span style={{ color: 'var(--color-muted)' }}>—</span>;

  const labels = {
    safe: t('dashboard.connector.toolScopeSafe'),
    full: t('dashboard.connector.toolScopeFull'),
  };
  const descriptions = {
    safe: t('dashboard.connector.toolScopeSafeDesc'),
    full: t('dashboard.connector.toolScopeFullDesc'),
  };

  return (
    <div>
      <span style={{ fontWeight: 500 }}>{labels[scope] || scope}</span>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-muted)', margin: '0.25rem 0 0', lineHeight: 1.4 }}>
        {descriptions[scope] || ''}
      </p>
    </div>
  );
}

export default function DashboardPage() {
  const { user, userAttributes } = useAuth();
  const { t, lang, setLanguage } = useTranslation();
  const lp = useLocalizedPath();
  const [status, setStatus] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);

  useEffect(() => {
    Promise.all([
      apiFetch('/account/status'),
      apiFetch('/onboard/connectors').catch(() => ({ connectors: [] })),
    ])
      .then(([s, c]) => {
        setStatus(s);
        setConnectors(c.connectors ?? []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function openBillingPortal() {
    setPortalLoading(true);
    setBillingError(null);
    try {
      const { url } = await apiFetch('/billing/portal', { method: 'POST' });
      window.location.href = url;
    } catch (e) {
      setBillingError(e.message);
    } finally {
      setPortalLoading(false);
    }
  }

  // Build display name from user attributes (with ID token fallback from useAuth)
  const displayName = userAttributes?.name || userAttributes?.given_name || null;
  const email = userAttributes?.email || null;
  const accountDisplay = displayName && email
    ? `${displayName} (${email})`
    : email || displayName || user?.username || '—';

  // Primary connector for gateway overview
  const primaryConnector = connectors.find(c => c.status === 'active') || (connectors.length > 0 ? connectors[0] : null);
  const connectorOnline = primaryConnector?.status === 'active';

  return (
    <>
      <PageMeta
        title={t('dashboard.meta.title')}
        description={t('dashboard.meta.description')}
        path="/dashboard"
      />

      <main id="main-content" className="page">
        <h1 className="page-title">{t('dashboard.pageTitle')}</h1>

        {loading && <div className="spinner" role="status" aria-label={t('dashboard.loading')} />}
        {error && (
          <div className="card" role="alert" style={{ color: 'var(--color-danger)', marginBottom: '1.5rem' }}>
            {t('dashboard.errorLoad', { error })}
          </div>
        )}

        {!loading && !error && status && (
          <>
            {/* Subscription */}
            <section aria-labelledby="subscription-heading" className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 id="subscription-heading" style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1rem', marginTop: 0 }}>
                {t('dashboard.subscription.heading')}
              </h2>
              <table style={{ borderCollapse: 'collapse' }} aria-label={t('dashboard.subscription.ariaLabel')}>
                <tbody>
                  <Row label={t('dashboard.subscription.statusLabel')}>
                    <StatusBadge status={status.subscriptionStatus} t={t} />
                  </Row>
                  {status.subscriptionExpiresAt && (
                    <Row label={t('dashboard.subscription.renewsLabel')}>
                      {new Date(status.subscriptionExpiresAt).toLocaleDateString()}
                    </Row>
                  )}
                  <Row label={t('dashboard.subscription.accountLabel')}>{accountDisplay}</Row>
                </tbody>
              </table>

              {billingError && (
                <div className="card" role="alert" style={{ color: 'var(--color-danger)', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
                  {t('dashboard.subscription.billingError', { error: billingError })}
                  <button
                    className="btn-link"
                    onClick={() => setBillingError(null)}
                    style={{ marginLeft: '0.75rem', fontSize: '0.8rem' }}
                    aria-label={t('dashboard.subscription.dismiss')}
                  >
                    {t('dashboard.subscription.dismiss')}
                  </button>
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                <button className="btn-secondary" onClick={openBillingPortal} disabled={portalLoading}
                  aria-busy={portalLoading}>
                  {portalLoading
                    ? t('dashboard.subscription.manageBillingLoading')
                    : t('dashboard.subscription.manageBilling')}
                </button>
              </div>
            </section>

            {/* Gateway — read-only overview */}
            <section aria-labelledby="connector-heading" className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 id="connector-heading" style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1rem', marginTop: 0 }}>
                {t('dashboard.connector.heading')}
              </h2>

              {primaryConnector ? (
                <>
                  <table style={{ borderCollapse: 'collapse' }} aria-label={t('dashboard.connector.ariaLabel')}>
                    <tbody>
                      {primaryConnector.name && (
                        <Row label={t('dashboard.connector.aliasLabel')}>
                          {primaryConnector.name}
                        </Row>
                      )}
                      <Row label={t('dashboard.connector.nameLabel')}>
                        <code style={{ fontSize: '0.85rem' }}>{primaryConnector.connectorId}</code>
                      </Row>
                      <Row label={t('dashboard.connector.statusLabel')}>
                        {connectorOnline
                          ? <span className="badge badge-success">{t('dashboard.connector.statusOnline')}</span>
                          : <span className="badge badge-danger">{t('dashboard.connector.statusOffline')}</span>}
                      </Row>
                      <Row label={t('dashboard.connector.toolScopeLabel')}>
                        <ToolScopeDisplay scope={status.toolScope} t={t} />
                      </Row>
                    </tbody>
                  </table>
                  <div style={{ marginTop: '1rem' }}>
                    <Link to={lp('/connector')} className="btn-secondary" style={{ display: 'inline-block' }}>
                      {t('dashboard.connector.editLink')}
                    </Link>
                  </div>
                </>
              ) : (
                <p style={{ color: 'var(--color-muted)', fontSize: '0.9rem', margin: 0 }}>
                  <span>{t('dashboard.connector.notConfigured')}</span>
                  <Link to={lp('/connector')} style={{ marginLeft: '0.75rem', fontSize: '0.85rem' }}>
                    {t('dashboard.connector.setupLink')}
                  </Link>
                </p>
              )}
            </section>

            {/* Quick Links */}
            <section aria-labelledby="quicklinks-heading" className="card">
              <h2 id="quicklinks-heading" style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1rem', marginTop: 0 }}>
                {t('dashboard.quickLinks.heading')}
              </h2>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <Link to={lp('/connector')} className="btn-secondary" style={{ display: 'inline-block' }}>
                  {t('dashboard.quickLinks.manageConnector')}
                </Link>
                <div className="dash-lang-picker">
                  <label htmlFor="dash-lang-select" className="dash-lang-label">
                    {t('dashboard.quickLinks.languageLabel')}
                  </label>
                  <select
                    id="dash-lang-select"
                    className="dash-lang-select"
                    value={lang}
                    onChange={e => setLanguage(e.target.value)}
                  >
                    {LANGUAGE_NAMES.map(({ code, native }) => (
                      <option key={code} value={code}>{native}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </>
  );
}
